import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupplierList from '../../components/supplier/SupplierList';
import SupplierForm from '../../components/supplier/SupplierForm';
import SupplierDetails from '../../components/supplier/SupplierDetails';
import { Supplier } from '../../types/supplier';
import { deleteSupplier } from '../../services/supplierService';

const SuppliersPage: React.FC = () => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | undefined>(undefined);
  const [showDetails, setShowDetails] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const handleAddClick = () => {
    setSelectedSupplier(undefined);
    setShowForm(true);
  };

  const handleEditClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowForm(true);
  };

  const handleRowClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDetails(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedSupplier(undefined);
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setSelectedSupplier(undefined);
    refreshData();
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedSupplier(undefined);
  };

  const handleDeleteSupplier = async (supplierId: string) => {
    if (window.confirm(t('suppliers.messages.confirmDelete'))) {
      try {
        await deleteSupplier(supplierId);
        handleBackToList();
        refreshData();
      } catch (error) {
        console.error('Error deleting supplier:', error);
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {showDetails && selectedSupplier ? (
          <>
            <Box display="flex" alignItems="center" mb={3}>
              <IconButton onClick={handleBackToList} size="large">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1">
                {selectedSupplier.name}
              </Typography>
            </Box>
            <SupplierDetails
              supplier={selectedSupplier}
              onUpdate={refreshData}
              onEditSupplier={handleEditClick}
              onDeleteSupplier={handleDeleteSupplier}
            />
          </>
        ) : (
          <SupplierList
            onAddClick={handleAddClick}
            onRowClick={handleRowClick}
            key={refreshKey}
          />
        )}

        <Dialog
          open={showForm}
          onClose={handleFormClose}
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            <SupplierForm
              supplier={selectedSupplier}
              onSubmit={handleFormSubmit}
              onCancel={handleFormClose}
            />
          </DialogContent>
        </Dialog>

      </Box>
    </Container>
  );
};

export default SuppliersPage;
